import {Moment} from "moment";

export enum LSAccountStatus {
  Requested = "requested",
  Active = "active",
  Suspended = "suspended",
  Blocked = "blocked",
  Disabled = "disabled",
  Deleted = "deleted",
}

export enum LSTransactionStatus {
  Created = "created",
  Signed = "signed",
  Sent = "sent",
  Executed = "Executed",
  Rejected = "rejected",
  Onhold = "onhold",
}

export enum LSTransactionType {
  European = "european",
  Intracountry = "intracountry",
  Internal = "internal",
  Fee = "fee",
  Own = "own",
}

export enum LSTransactionDirection {
  In = "in",
  Out = "out",
  Exchange = "exchange",
}

export type LSGetAccountsParams = {
  type?: "fiat" | "crypto";
  full?: boolean;
  status?: LSAccountStatus[];
};

export type LSFiatAccountDetails = {
  name: string;
  iban: string;
  swift: string;
  countryCode: string;
};

export type LSAccount = {
  id: string;
  currency: string;
  type: "fiat" | "crypto";
  status: LSAccountStatus;
  balance: LSBalance;
  name: string;
  createdAt: string;
  fiat?: LSFiatAccountDetails;
};

export type LSBalance = {
  totalAmount: number;
  onholdAmount: number;
  availableAmount: number;
  currencyCode: string;
};

export type LSGetTransactionsParams = {
  accountId?: string;
  limit?: number;
  page?: number;
  before?: string;
  after?: string;
  status?: LSTransactionStatus[];
  orderBy?: string;
};

export type LSAmount = {
  amount: number;
  currency: string;
};

export type LSTransactionDocumentsShort = {
  documentId: string;
  name: string;
};

export type LSTransactionShort = {
  transactionId: string;
  type: LSTransactionType;
  createdAt: Moment;
  signedAt: Moment | null;
  executedAt: Moment | null;
  fromAccountId?: string;
  toAccountId?: string;
  direction: LSTransactionDirection;
  status: LSTransactionStatus;
  description: string;
  amount: LSAmount;
  feeAmount: LSAmount;
  documents: LSTransactionDocumentsShort[];
  from: LSFiatAccountDetails;
  to: LSFiatAccountDetails;
};

export type LSList<T> = {
  list: T[];
  meta: {
    records: number;
    currentPage: number;
    lastPage: number;
    minEntry: number;
    maxEntry: number;
  };
};

export type LSAccountsList = LSAccount[];

export default interface ILedgerRepository {
  getAccounts(params: LSGetAccountsParams): Promise<LSAccountsList>;
  getAccount(accountId: string): Promise<LSAccount>;
  getTransactions(
    params: LSGetTransactionsParams,
  ): Promise<LSList<LSTransactionShort>>;
  getTotalBalance(accountGroup: string, currency: string): Promise<LSBalance>;
  getTotalBalances(currency: string): Promise<LSBalance[]>;
  
  // Transaction methods
  sendCrypto(params: {
    fromAccount: string;
    toAddress: string;
    amount: number;
    currency: string;
    networkFee: number;
    memo?: string;
  }): Promise<{ transactionHash: string; transactionId: string }>;

  buyCrypto(params: {
    cryptocurrency: string;
    amount: number;
    paymentMethod: string;
    fiatAmount: number;
  }): Promise<{ transactionId: string; purchaseId: string }>;

  exchangeCrypto(params: {
    fromCurrency: string;
    toCurrency: string;
    fromAmount: number;
    toAmount: number;
    slippageTolerance: number;
  }): Promise<{ transactionHash: string; exchangeId: string }>;

  sendCash(params: {
    fromAccount: string;
    recipientName: string;
    recipientType: 'bank' | 'email' | 'phone';
    recipientDetails: any;
    amount: number;
    currency: string;
    transferMethod: string;
    memo?: string;
  }): Promise<{ transactionId: string; transferId: string }>;

  orderCard(params: {
    cardType: 'virtual' | 'physical' | 'premium';
    cardholderName: string;
    linkedAccount: string;
    design?: string;
    shippingAddress?: any;
    settings: any;
  }): Promise<{ orderNumber: string; cardId: string }>;

  topUpAccount(params: {
    toAccount: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    paymentDetails: any;
  }): Promise<{ transactionId: string; topUpId: string }>;

  getTransactionHistory(params: {
    accountId?: string;
    startDate?: Date;
    endDate?: Date;
    transactionType?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<LSList<LSTransactionShort>>;
}
